import React from 'react';

/**
 * AnimatedMenuIcon — pure SVG via innerHTML to bypass dev visual-editor span wrappers
 * (which break SVG rendering by inserting non-SVG <span> nodes between <svg> children).
 */

const FLAME = '#D85308';
const EMBER = '#BC3F05';

let injected = false;
const ensureKeyframes = () => {
 if (injected || typeof document === 'undefined') return;
 const css = `
 @keyframes ami-bob1 { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-2px)} }
 @keyframes ami-bob2 { 0%,100%{transform:translateY(0)} 50%{transform:translateY(2px)} }
 @keyframes ami-bob3 { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-3px)} }
 @keyframes ami-bob4 { 0%,100%{transform:translateY(0)} 50%{transform:translateY(3px)} }
 @keyframes ami-spin { to { transform: rotate(360deg) } }
 @keyframes ami-pulse { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.18);opacity:.85} }
 @keyframes ami-pulse-soft { 0%,100%{opacity:1} 50%{opacity:.55} }
 @keyframes ami-coin { 0%,100%{transform:scaleX(1)} 50%{transform:scaleX(.18)} }
 @keyframes ami-dash { to { stroke-dashoffset: -16 } }
 @keyframes ami-flicker { 0%,100%{opacity:.2} 50%{opacity:1} }
 @keyframes ami-rocket { 0%,100%{transform:translate(0,0) rotate(-6deg)} 50%{transform:translate(2px,-2px) rotate(-12deg)} }
 @keyframes ami-slide-r { 0%,100%{transform:translateX(0)} 50%{transform:translateX(8px)} }
 @keyframes ami-slide-l { 0%,100%{transform:translateX(0)} 50%{transform:translateX(-8px)} }
 @keyframes ami-burst { 0%,100%{transform:scale(.95)} 50%{transform:scale(1.12)} }
 @keyframes ami-fill { 0%{stroke-dashoffset:60} 60%{stroke-dashoffset:0} 100%{stroke-dashoffset:0} }
 @keyframes ami-grow1 { 0%,100%{transform:scaleY(.7)} 50%{transform:scaleY(1)} }
 @keyframes ami-grow2 { 0%,100%{transform:scaleY(.65)} 50%{transform:scaleY(1)} }
 @keyframes ami-grow3 { 0%,100%{transform:scaleY(.6)} 50%{transform:scaleY(1)} }
 .ami-tb { transform-box: fill-box; transform-origin: center; }
 .ami-bottom { transform-box: fill-box; transform-origin: bottom; }
 .ami-left { transform-box: fill-box; transform-origin: left center; }
 `;
 const tag = document.createElement('style');
 tag.setAttribute('data-ami', 'true');
 tag.textContent = css;
 document.head.appendChild(tag);
 injected = true;
};

// SVG inner-content strings (no JSX, no .map → no dev-wrapper injection)
const ICON_SVG = {
 spot: `
 <g class="ami-tb" style="animation:ami-bob1 2.2s ease-in-out infinite">
 <line x1="10" y1="14" x2="10" y2="36" stroke="${FLAME}" stroke-width="1.4"/>
 <rect x="8" y="20" width="4" height="14" rx="1" fill="${FLAME}"/>
 </g>
 <g class="ami-tb" style="animation:ami-bob2 2.2s ease-in-out .2s infinite">
 <line x1="20" y1="9" x2="20" y2="38" stroke="${EMBER}" stroke-width="1.4"/>
 <rect x="18" y="14" width="4" height="20" rx="1" fill="${EMBER}"/>
 </g>
 <g class="ami-tb" style="animation:ami-bob3 2.2s ease-in-out .4s infinite">
 <line x1="30" y1="16" x2="30" y2="36" stroke="${FLAME}" stroke-width="1.4"/>
 <rect x="28" y="22" width="4" height="12" rx="1" fill="${FLAME}"/>
 </g>
 <g class="ami-tb" style="animation:ami-bob4 2.2s ease-in-out .6s infinite">
 <line x1="40" y1="11" x2="40" y2="36" stroke="${EMBER}" stroke-width="1.4"/>
 <rect x="38" y="16" width="4" height="18" rx="1" fill="${EMBER}"/>
 </g>`,

 futures: `
 <g class="ami-tb" style="animation:ami-rocket 1.6s ease-in-out infinite">
 <path d="M30 12 L36 14 L34 20 L26 22 Z" fill="${FLAME}"/>
 <path d="M26 22 L20 28 L22 34 L28 28 Z" fill="${EMBER}"/>
 <circle cx="32" cy="16" r="1.6" fill="#fff"/>
 </g>
 <circle cx="16" cy="32" r="2" fill="${FLAME}" style="animation:ami-flicker .9s ease-in-out infinite"/>
 <circle cx="12" cy="36" r="1.4" fill="${EMBER}" style="animation:ami-flicker .9s ease-in-out .3s infinite"/>`,

 margin: `
 <rect x="8" y="22" width="32" height="6" rx="3" fill="rgba(255,122,0,0.18)"/>
 <rect x="8" y="22" width="22" height="6" rx="3" fill="${FLAME}" class="ami-left" style="animation:ami-grow1 2.4s ease-in-out infinite"/>
 <text x="6" y="14" font-family="ui-monospace,monospace" font-size="6" font-weight="800" fill="${FLAME}">x</text>
 <text x="11" y="14" font-family="ui-monospace,monospace" font-size="6" font-weight="800" fill="${EMBER}" style="animation:ami-pulse-soft 1.2s ease-in-out infinite">125</text>
 <circle cx="30" cy="25" r="3.5" fill="${EMBER}" class="ami-tb" style="animation:ami-pulse 1.6s ease-in-out infinite"/>`,

 copy: `
 <line x1="16" y1="24" x2="32" y2="14" stroke="${FLAME}" stroke-width="1.6" stroke-dasharray="3 3" style="animation:ami-dash 1.2s linear infinite"/>
 <line x1="16" y1="24" x2="32" y2="34" stroke="${EMBER}" stroke-width="1.6" stroke-dasharray="3 3" style="animation:ami-dash 1.2s linear .3s infinite"/>
 <circle cx="16" cy="24" r="4.5" fill="${FLAME}" class="ami-tb" style="animation:ami-pulse 1.4s ease-in-out infinite"/>
 <circle cx="32" cy="14" r="3.5" fill="${EMBER}" class="ami-tb" style="animation:ami-pulse 1.4s ease-in-out .4s infinite"/>
 <circle cx="32" cy="34" r="3.5" fill="${EMBER}" class="ami-tb" style="animation:ami-pulse 1.4s ease-in-out .7s infinite"/>`,

 bots: `
 <g class="ami-tb" style="animation:ami-spin 6s linear infinite">
 <path d="M24 8 L26 12 L30 11 L31 15 L34 17 L33 21 L36 24 L33 27 L34 31 L31 33 L30 37 L26 36 L24 40 L22 36 L18 37 L17 33 L14 31 L15 27 L12 24 L15 21 L14 17 L17 15 L18 11 L22 12 Z" fill="${FLAME}"/>
 <circle cx="24" cy="24" r="6" fill="#fff"/>
 <circle cx="24" cy="24" r="3" fill="${EMBER}"/>
 </g>`,

 p2p: `
 <circle cx="12" cy="24" r="5" fill="${FLAME}"/>
 <circle cx="36" cy="24" r="5" fill="${EMBER}"/>
 <path d="M18 18 L26 18 L26 16 L30 19 L26 22 L26 20 L18 20 Z" fill="${FLAME}" style="animation:ami-slide-r 1.6s ease-in-out infinite"/>
 <path d="M30 30 L22 30 L22 28 L18 31 L22 34 L22 32 L30 32 Z" fill="${EMBER}" style="animation:ami-slide-l 1.6s ease-in-out .8s infinite"/>`,

 crypto: `
 <g class="ami-tb" style="animation:ami-coin 3s ease-in-out infinite">
 <circle cx="24" cy="24" r="14" fill="${FLAME}" stroke="${EMBER}" stroke-width="2"/>
 <text x="24" y="29" text-anchor="middle" font-family="ui-monospace,monospace" font-size="14" font-weight="900" fill="#fff">₿</text>
 </g>`,

 forex: `
 <text x="14" y="20" font-family="ui-monospace,monospace" font-size="11" font-weight="900" fill="${FLAME}" style="animation:ami-bob1 1.6s ease-in-out infinite">€</text>
 <text x="28" y="36" font-family="ui-monospace,monospace" font-size="11" font-weight="900" fill="${EMBER}" style="animation:ami-bob2 1.6s ease-in-out .4s infinite">$</text>
 <path d="M22 16 L34 22" stroke="${FLAME}" stroke-width="1.6" stroke-linecap="round" stroke-dasharray="14" style="animation:ami-fill 2s ease-in-out infinite"/>
 <path d="M14 30 L26 36" stroke="${EMBER}" stroke-width="1.6" stroke-linecap="round" stroke-dasharray="14" style="animation:ami-fill 2s ease-in-out .5s infinite"/>`,

 indices: `
 <rect x="10" y="28" width="6" height="10" rx="1" fill="${FLAME}" class="ami-bottom" style="animation:ami-grow1 2s ease-in-out infinite"/>
 <rect x="20" y="22" width="6" height="16" rx="1" fill="${EMBER}" class="ami-bottom" style="animation:ami-grow2 2s ease-in-out .2s infinite"/>
 <rect x="30" y="16" width="6" height="22" rx="1" fill="${FLAME}" class="ami-bottom" style="animation:ami-grow3 2s ease-in-out .4s infinite"/>
 <path d="M13 28 L23 22 L33 16" stroke="${EMBER}" stroke-width="1.6" stroke-linecap="round" fill="none" stroke-dasharray="28" style="animation:ami-fill 2s ease-in-out infinite"/>`,

 listings: `
 <g class="ami-tb" style="animation:ami-burst 1.4s ease-in-out infinite">
 <path d="M24 10 L26 22 L38 24 L26 26 L24 38 L22 26 L10 24 L22 22 Z" fill="${FLAME}"/>
 </g>
 <circle cx="40" cy="24" r="1.6" fill="${EMBER}" style="animation:ami-flicker 1.6s ease-in-out 0s infinite"/>
 <circle cx="32" cy="38" r="1.6" fill="${EMBER}" style="animation:ami-flicker 1.6s ease-in-out .15s infinite"/>
 <circle cx="16" cy="38" r="1.6" fill="${EMBER}" style="animation:ami-flicker 1.6s ease-in-out .3s infinite"/>
 <circle cx="8" cy="24" r="1.6" fill="${EMBER}" style="animation:ami-flicker 1.6s ease-in-out .45s infinite"/>
 <circle cx="16" cy="10" r="1.6" fill="${EMBER}" style="animation:ami-flicker 1.6s ease-in-out .6s infinite"/>
 <circle cx="32" cy="10" r="1.6" fill="${EMBER}" style="animation:ami-flicker 1.6s ease-in-out .75s infinite"/>`,

 analysis: `
 <path d="M8 32 Q14 22, 18 26 T26 20 T36 12" stroke="${FLAME}" stroke-width="2" stroke-linecap="round" fill="none" stroke-dasharray="48" style="animation:ami-fill 3s ease-in-out infinite"/>
 <rect x="34" y="22" width="3" height="10" rx="1" fill="${EMBER}" transform="rotate(45 35.5 27)" style="animation:ami-bob3 2s ease-in-out infinite"/>`,

 bonus: `
 <g class="ami-tb" style="animation:ami-bob1 2.4s ease-in-out infinite">
 <rect x="10" y="20" width="28" height="20" rx="2" fill="${FLAME}"/>
 <rect x="22" y="20" width="4" height="20" fill="#fff"/>
 <rect x="10" y="14" width="28" height="6" rx="1" fill="${EMBER}"/>
 <rect x="22" y="14" width="4" height="6" fill="#fff"/>
 <path d="M24 14 C20 8 14 8 16 14 M24 14 C28 8 34 8 32 14" stroke="${EMBER}" stroke-width="1.6" fill="none"/>
 </g>`,

 default: `
 <circle cx="24" cy="24" r="10" fill="${FLAME}" class="ami-tb" style="animation:ami-pulse 1.6s ease-in-out infinite"/>`,
};

export const AnimatedMenuIcon = ({ kind = 'default', className = '' }) => {
 ensureKeyframes();
 const inner = ICON_SVG[kind] || ICON_SVG.default;
 return (
 <svg
 viewBox="0 0 48 48"
 className={`block w-full h-full ${className}`}
 fill="none"
 xmlns="http://www.w3.org/2000/svg"
 aria-hidden="true"
 dangerouslySetInnerHTML={{ __html: inner }}
 />
 );
};

export default AnimatedMenuIcon;
